import React from 'react';
import { ArrowLeft } from 'lucide-react';

/**
 * Product image gallery with click-to-advance slideshow and thumbnail strip.
 */
const ProductGallery = ({ product, selectedImage, setSelectedImage }) => {
  const productImages = product.images || [product.image];
  const currentImage = productImages[selectedImage];
  const hasMultiple = productImages.length > 1;
  const goNext = () => setSelectedImage((i) => (i + 1) % productImages.length);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goNext();
    }
  };

  return (
    <div className="group relative">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Main image */}
      <div
        className={`relative bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all mb-4 ${hasMultiple ? 'cursor-pointer select-none' : ''}`}
        onClick={hasMultiple ? goNext : undefined}
        role={hasMultiple ? 'button' : undefined}
        tabIndex={hasMultiple ? 0 : undefined}
        onKeyDown={hasMultiple ? handleKeyDown : undefined}
        data-testid="product-slideshow"
        aria-label={hasMultiple ? 'Click to view next image' : undefined}
      >
        <img
          src={currentImage}
          alt={product.name}
          className="w-full h-full object-cover transition-opacity duration-300"
          key={selectedImage}
        />
        {hasMultiple && (
          <>
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-medium">
              {selectedImage + 1} / {productImages.length}
            </div>
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-700 text-xs font-medium shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
              Click for next
              <ArrowLeft className="h-3 w-3 rotate-180" />
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {hasMultiple && (
        <div className="flex gap-3">
          {productImages.map((img, index) => (
            <button
              key={img}
              onClick={() => setSelectedImage(index)}
              className={`relative flex-1 bg-white border-2 rounded-lg overflow-hidden transition-all hover:shadow-md ${
                selectedImage === index
                  ? 'border-cyan-500 ring-2 ring-cyan-200'
                  : 'border-gray-200 hover:border-cyan-300'
              }`}
              data-testid={`product-thumbnail-${index}`}
            >
              <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-20 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
